import type { IndustryVertical } from "~/lib/utils/industry-profile";
import type { HireRecommendationsSelection } from "~/lib/marketplace/hire-recommendations-pricing";
import {
  defaultHireDepartmentsForVertical,
  type HireLocationDetail,
} from "~/lib/marketplace/hire-recommendations-pricing";
import { buildDefaultHireLocationDetail } from "~/lib/marketplace/hire-location-utils";
import {
  buildDefaultHireDepartmentDetail,
  ensureHireDepartmentDetailsInSelection,
} from "~/lib/marketplace/hire-department-utils";

export type HireOrganizationLayoutMode = "locations" | "departments";

const DEPARTMENT_ORG_VERTICALS: IndustryVertical[] = ["education", "nonprofit"];

/** Multi-discipline engineering / MEP firms organize by department at each office. */
const MEP_DEPARTMENT_KEYWORDS =
  /\b(mep|mechanical engineering|electrical engineering|plumbing engineering|fire protection|life safety|commissioning|built environment|engineering firm|engineering company)\b/i;

/** Colleges and nonprofits organize by department. Other verticals stay location-first. */
export function resolveHireOrganizationLayout(
  vertical: IndustryVertical,
  contentHint = "",
): HireOrganizationLayoutMode {
  if (DEPARTMENT_ORG_VERTICALS.includes(vertical)) return "departments";
  if (vertical === "consulting" && MEP_DEPARTMENT_KEYWORDS.test(contentHint)) {
    return "departments";
  }
  return "locations";
}

/**
 * Always expose Locations and Departments in Organization UI.
 * Layout mode still controls defaults/roster emphasis, but users must be able
 * to review and Generate-with-AI locations even when the firm is department-first
 * (colleges, nonprofits, MEP) or when the scrape returned 0 locations.
 */
export function getOrganizationVisibleSections(
  _layout: HireOrganizationLayoutMode,
): Array<"team" | "locations" | "departments"> {
  return ["team", "locations", "departments"];
}

export function applyOrganizationLayoutToSelection(
  selection: HireRecommendationsSelection,
  layout: HireOrganizationLayoutMode,
  args: {
    vertical: IndustryVertical;
    businessName: string;
    contentHint?: string;
    leadTeamMemberEmail?: string;
    phone?: string;
  },
): HireRecommendationsSelection {
  const departmentDefaults = defaultHireDepartmentsForVertical(
    args.vertical,
    args.contentHint ?? args.businessName,
  );

  if (layout === "departments") {
    // Keep multiple scraped offices when present (e.g. BCER CO + FL) while still
    // creating discipline departments on the primary location.
    const dedupedLocations =
      selection.locationDetails.length > 0
        ? selection.locationDetails
        : [
            {
              ...buildDefaultHireLocationDetail({
                companyName: args.businessName,
                leadTeamMemberEmail: args.leadTeamMemberEmail,
                phone: args.phone,
              }),
              name: args.businessName,
            },
          ];
    const locationCount = Math.max(1, dedupedLocations.length);
    const existingNamed =
      selection.departmentDetails.length > 0 &&
      selection.departmentDetails.some((dept) => dept.name?.trim())
        ? selection.departmentDetails
        : null;
    // Replace mistaken vertical defaults when Organization Profile has better departments.
    const looksLikeSchoolDefaults =
      !!existingNamed &&
      existingNamed.length >= 3 &&
      existingNamed.every((d) =>
        /^(admissions|academics|student life|athletics)$/i.test(d.name.trim()),
      );
    const looksLikeHealthcareDefaults =
      !!existingNamed &&
      existingNamed.length <= 3 &&
      existingNamed.every((d) => /^(front desk|clinical)$/i.test(d.name.trim()));
    const profileHasOfferings = /##\s*service offerings/i.test(args.contentHint ?? "");
    const useDefaults =
      !existingNamed ||
      ((looksLikeSchoolDefaults || looksLikeHealthcareDefaults) &&
        (args.vertical === "nonprofit" ||
          profileHasOfferings ||
          departmentDefaults.length >= 2));
    const existingDepartments = useDefaults
      ? departmentDefaults.map((dept) =>
          buildDefaultHireDepartmentDetail({
            name: dept.name,
            leadTeamMemberEmail: args.leadTeamMemberEmail,
          }),
        )
      : existingNamed;
    return ensureHireDepartmentDetailsInSelection(
      {
        ...selection,
        locationCount,
        locationDetails: dedupedLocations.slice(0, locationCount),
        locationList: dedupedLocations.slice(0, locationCount).map((loc) => loc.name),
        departmentCount: Math.max(existingDepartments.length, selection.departmentCount, 1),
        departmentDetails: existingDepartments,
      },
      { leadTeamMemberEmail: args.leadTeamMemberEmail },
    );
  }

  const dedupedLocations =
    selection.locationDetails.length > 0
      ? selection.locationDetails
      : [buildDefaultHireLocationDetail({ companyName: args.businessName })];
  const locationCount = Math.max(1, dedupedLocations.length);

  return {
    ...selection,
    locationCount,
    locationDetails: dedupedLocations.slice(0, locationCount),
    locationList: dedupedLocations.slice(0, locationCount).map((loc) => loc.name),
    departmentCount: 1,
    departmentDetails: [{ name: departmentDefaults[0]?.name ?? "Front Desk" }],
  };
}
